import { LitElement, html, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import { RovingTabindexController } from '@/controllers/roving-tabindex.controller'
import { GalleryEvent } from '@/models/gallery-event'
import { contentTypeToSvg } from '@/utils/content-type-svg'
import type { FileModel } from '@/models/gallery.model'
import styles from './gallery-tiles-view.styles.scss?inline'

/**
 * Tile grid view for the GoTo Gallery SPA.
 *
 * Renders files in a 4-column CSS Grid with square thumbnail tiles.
 * Files with a `thumbnailUrl` show the image; otherwise a Chameleon SVG
 * icon matching the content type is shown as a fallback.
 *
 * Keyboard navigation is managed by `RovingTabindexController`:
 * - `ArrowRight` / `ArrowLeft` / `ArrowUp` / `ArrowDown` move focus.
 * - `Space` emits `file-select` for the active tile.
 * - `Enter` emits `file-open` for the active tile.
 *
 * @prop files       - Array of files to display.
 * @prop selectedIds - Set of currently selected file IDs.
 *
 * @fires file-open   - `CustomEvent<number>` emitted with the file index on click or Enter.
 * @fires file-select - `CustomEvent<string>` emitted with the file ID on checkbox change or Space.
 */
export class GalleryTilesView extends LitElement {
  static readonly tagName = 'gallery-tiles-view'
  static styles = unsafeCSS(styles)

  @property({ attribute: false }) files: FileModel[] = []
  @property({ attribute: false }) selectedIds: Set<string> = new Set()

  private _roving = new RovingTabindexController(this, { selector: '.tile', columns: 4 })

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('keydown', this._handleKeydown)
  }

  override disconnectedCallback(): void {
    this.removeEventListener('keydown', this._handleKeydown)
    super.disconnectedCallback()
  }

  /**
   * Moves focus to the tile at the given index.
   * Called by the parent shell after detail view closes to restore keyboard focus.
   *
   * @param index - Zero-based index of the tile to focus.
   */
  setActiveIndex(index: number): void {
    this._roving.setActiveIndex(index)
  }

  private _handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ') {
      event.preventDefault()
      const file = this.files[this._roving.activeIndex]
      if (file) {
        this._onCheckboxChange(file.id)
      }
    } else if (event.key === 'Enter') {
      this._onTileClick(this._roving.activeIndex)
    }
  }

  private _onTileClick(index: number): void {
    this.dispatchEvent(
      new CustomEvent<number>(GalleryEvent.FILE_OPEN, { detail: index, bubbles: true, composed: true }),
    )
  }

  private _onCheckboxChange(id: string): void {
    this.dispatchEvent(new CustomEvent<string>(GalleryEvent.FILE_SELECT, { detail: id, bubbles: true, composed: true }))
  }

  private _onTileKeydown(event: KeyboardEvent, fileId: string, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      this._onTileClick(index)
      return
    }

    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      event.stopPropagation()
      this._onCheckboxChange(fileId)
    }
  }

  private _renderTile(file: FileModel, index: number) {
    const isSelected = this.selectedIds.has(file.id)

    const thumbnail = file.thumbnailUrl
      ? html`<div class="tile-thumb" style="background-image: url('${file.thumbnailUrl}')"></div>`
      : html`<chameleon-svg class="tile-file-icon" style="--goto-icon-size: 40px"
          >${unsafeSVG(contentTypeToSvg(file.contentType))}</chameleon-svg
        >`

    return html`
      <div
        class="tile"
        role="gridcell"
        aria-selected="${isSelected ? 'true' : 'false'}"
        aria-label="${file.name}"
        @click=${() => this._onTileClick(index)}
        @keydown=${(event: KeyboardEvent) => this._onTileKeydown(event, file.id, index)}
      >
        ${thumbnail}
        <chameleon-checkbox
          class="tile-checkbox"
          .checked=${isSelected}
          @click=${(e: Event) => e.stopPropagation()}
          @keydown=${(e: KeyboardEvent) => e.stopPropagation()}
          @change=${(e: Event) => {
            e.stopPropagation()
            this._onCheckboxChange(file.id)
          }}
        ></chameleon-checkbox>
      </div>
    `
  }

  render() {
    return html` <div class="tiles-grid" role="grid">${this.files.map((file, i) => this._renderTile(file, i))}</div> `
  }
}
